import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import { Moon, Sun } from 'lucide-react';
import HabitTracker from './components/HabitTracker';
import { Toaster } from './components/ui/toaster';
import { supabase } from './lib/supabaseClient';

import Auth from './components/Auth';
import LandingPage from './components/LandingPage';
import SuccessPage from './components/SuccessPage';
import InstallPrompt from './components/InstallPrompt';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfUse from './components/TermsOfUse';
import LandingPageV1 from './components/LandingPageV1';
import LandingPageV2 from './components/LandingPageV2';
import LandingPageV3 from './components/LandingPageV3';
import WhatsAppButton from './components/WhatsAppButton';
import logo2 from './assets/logo2.png';

function ThemeToggle({ theme, toggleTheme }) {
  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg hover:bg-accent transition-colors"
      aria-label="Toggle theme"
    >
      {theme === 'light' ? (
        <Moon className="w-5 h-5 text-foreground" />
      ) : (
        <Sun className="w-5 h-5 text-foreground" />
      )}
    </button>
  );
}

function AppContent() {
  const [theme, setTheme] = useState('light');
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    setTheme(savedTheme);
    document.documentElement.classList.toggle('dark', savedTheme === 'dark');

    // Supabase Session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    document.documentElement.classList.toggle('dark', newTheme === 'dark');
  };

  if (loading) return null;

  return (
    <div className="min-h-screen bg-background transition-colors duration-300">
      <Routes>
        <Route
          path="/v1"
          element={
            <LandingPageV1 onGetStarted={() => navigate('/login')} />
          }
        />

        <Route
          path="/v2"
          element={
            <LandingPageV2 onGetStarted={() => navigate('/login')} />
          }
        />

        <Route
          path="/v3"
          element={
            <LandingPageV3 onGetStarted={() => navigate('/login')} />
          }
        />

        <Route
          path="/"
          element={
            !session ? (
              <LandingPage onGetStarted={() => navigate('/login')} />
            ) : (
              <Navigate to="/app" />
            )
          }
        />

        <Route
          path="/login"
          element={
            !session ? (
              <div className="container mx-auto px-4 py-6 max-w-7xl flex flex-col items-center justify-center min-h-screen">
                <div className="absolute top-4 right-4">
                  <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
                </div>
                <Auth />
              </div>
            ) : (
              <Navigate to="/app" />
            )
          }
        />

        <Route
          path="/success"
          element={<SuccessPage />}
        />

        <Route
          path="/privacy"
          element={<PrivacyPolicy />}
        />

        <Route
          path="/terms"
          element={<TermsOfUse />}
        />

        <Route
          path="/app"
          element={
            session ? (
              <div className="container mx-auto px-4 py-6 max-w-7xl">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex flex-col gap-1">
                    <img src={logo2} alt="SkipLater" className="w-[180px] h-auto" />
                    <p className="text-muted-foreground text-sm px-1">Build better habits, one day at a time</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => supabase.auth.signOut()}
                      className="text-sm text-muted-foreground hover:text-foreground"
                    >
                      Sair
                    </button>
                    <ThemeToggle theme={theme} toggleTheme={toggleTheme} />
                  </div>
                </div>
                <HabitTracker key={session.user.id} session={session} />
              </div>
            ) : (
              <Navigate to="/login" />
            )
          }
        />
      </Routes>
      <Toaster />
      <InstallPrompt session={session} />
      <WhatsAppButton />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
